// PRD 05 §3.2 — three top-line badges on a question card.
// Rules:
//   - target: always rendered.
//   - gender: hidden when COMMON; otherwise shows the bordered chip.
//   - applicability scope: always rendered; the disease label is appended for
//     non-COMMON scopes once disease lookup is available (passed in).

import { useTranslation } from 'react-i18next';

import type {
  Applicability,
  Gender,
  Target,
} from '../types/questionnaire-design.types';

const TARGET_KEY: Record<Target, string> = {
  PATIENT: 'enums.target.patient',
  CAREGIVER: 'enums.target.caregiver',
};

const TARGET_CHIP_CLASS: Record<Target, string> = {
  PATIENT: 'badge-primary',
  CAREGIVER: 'badge-warning',
};

const GENDER_KEY: Record<Gender, string> = {
  COMMON: 'enums.gender.common',
  MALE_ONLY: 'enums.gender.maleOnly',
  FEMALE_ONLY: 'enums.gender.femaleOnly',
};

const APPLICABILITY_KEY: Record<Applicability, string> = {
  COMMON: 'enums.applicability.common',
  DISEASE_GROUP: 'enums.applicability.group',
  DISEASE_SPECIFIC: 'enums.applicability.specific',
};

// Borrowed from the prototype's `genderBadgeCls`/`scopeBadgeCls` color logic.
const GENDER_OUTLINE: Record<Gender, { color: string; border: string }> = {
  COMMON: { color: 'var(--muted-foreground)', border: 'var(--border)' },
  MALE_ONLY: { color: 'var(--info)', border: 'var(--info)' },
  FEMALE_ONLY: { color: '#D6336C', border: '#D6336C' },
};

const APPLICABILITY_CHIP_CLASS: Record<Applicability, string> = {
  COMMON: 'badge-secondary',
  DISEASE_GROUP: 'badge-primary',
  DISEASE_SPECIFIC: 'badge-success',
};

interface QuestionBadgesProps {
  target: Target;
  gender: Gender;
  applicability: Applicability;
  // Optional disease label (e.g. 'F32 우울 에피소드'); appended after the scope
  // word once the disease lookup is wired through. Omitting it keeps the
  // bare scope label visible without breaking anything.
  diseaseLabel?: string;
}

export function QuestionBadges({
  target,
  gender,
  applicability,
  diseaseLabel,
}: QuestionBadgesProps) {
  const { t } = useTranslation('questionnaire-design');
  return (
    <div
      style={{
        padding: 'var(--space-3) var(--space-6)',
        display: 'flex',
        gap: 'var(--space-2)',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <span
        className={`badge ${TARGET_CHIP_CLASS[target]}`}
        style={{ fontSize: 'var(--font-size-xs)', padding: '3px 10px' }}
      >
        {t(TARGET_KEY[target])}
      </span>

      {gender !== 'COMMON' ? (
        <span
          className="badge"
          style={{
            fontSize: 'var(--font-size-xs)',
            padding: '3px 10px',
            background: 'transparent',
            color: GENDER_OUTLINE[gender].color,
            border: `1.5px solid ${GENDER_OUTLINE[gender].border}`,
          }}
        >
          {t(GENDER_KEY[gender])}
        </span>
      ) : null}

      <span
        className={`badge ${APPLICABILITY_CHIP_CLASS[applicability]}`}
        style={{ fontSize: 'var(--font-size-xs)', padding: '3px 10px' }}
      >
        {t(APPLICABILITY_KEY[applicability])}
        {diseaseLabel ? ` · ${diseaseLabel}` : ''}
      </span>
    </div>
  );
}
